// What a restaurant's Orders page works from: the areas, the tables, every open
// order and the guests' requests. Loaded once by the page (`ServiceLayout`) and
// handed to the tab that is open.
import type { GuestRequest, KitchenTicket, OrderDetail, PaidOrder, RowOf } from '@ims/data'
import { ticketStanding } from '@ims/core'
import { useMemo } from 'react'
import { useOutletContext } from 'react-router'
import { useLive, useNow } from '../hooks.ts'

export type DiningTable = RowOf<'dining_tables'>
export type DiningArea = RowOf<'dining_areas'>

export interface Floor {
  areas: DiningArea[]
  tables: DiningTable[]
  orders: OrderDetail[]
  /** Guests' requests from the table codes, waiting for a waiter. */
  requests: GuestRequest[]
}

/** Table names sort like people count them: T2 before T10. */
export const byName = (
  a: { name: string; sort_order: number },
  b: { name: string; sort_order: number },
) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, undefined, { numeric: true })

export function useFloor(): Floor | undefined {
  return useLive(
    async (r) => {
      const [areas, tables, orders, requests] = await Promise.all([
        r.list('dining_areas', { limit: 200 }),
        r.list('dining_tables', { limit: 1000 }),
        r.orders.open(),
        r.orders.requests(),
      ])
      return {
        areas: areas.rows.sort(byName),
        tables: tables.rows.sort(byName),
        orders,
        requests,
      }
    },
    [],
    [
      'dining_areas',
      'dining_tables',
      'orders',
      'order_items',
      'kot_tickets',
      'order_requests',
      'order_request_items',
    ],
  ).data
}

/**
 * Tickets the kitchen has not marked ready, oldest first, without those whose order was paid or
 * cancelled over an hour ago (by the clock: no data changes when they go).
 */
export function useKitchenTickets(): KitchenTicket[] | undefined {
  const all = useLive(
    (r) => r.orders.kitchen(),
    [],
    ['kot_tickets', 'kot_items', 'order_items', 'orders', 'dining_tables'],
  ).data
  const nowMs = useNow().getTime()
  // Only a ticket going makes a new list: the screens use it as an effect's dependency.
  const gone = (all ?? [])
    .filter((t) => ticketStanding(t.order, nowMs) === 'gone')
    .map((t) => t.ticket.id)
    .join()
  return useMemo(() => all?.filter((t) => !gone.includes(t.ticket.id)), [all, gone])
}

/**
 * The orders checked out with a bill dated `date`, newest first. Loaded only while `on`
 * (the Paid today view is open): it reads each order's bill and payments.
 */
export function usePaidOrders(date: string, on: boolean): PaidOrder[] | undefined {
  const paid = useLive(
    (r) => (on ? r.orders.paidOn(date) : Promise.resolve(null)),
    [date, on],
    ['orders', 'sales_invoices', 'payments', 'payment_allocations', 'dining_tables'],
  ).data
  return paid ?? undefined
}

export interface Service {
  floor: Floor
  /** Undefined while the tickets load. */
  tickets: KitchenTicket[] | undefined
  /** Today's paid orders: undefined unless the Paid today view is open and loaded. */
  paid: PaidOrder[] | undefined
}

/** The Orders page's data, inside one of its tabs. */
export function useService(): Service {
  return useOutletContext<Service>()
}
